import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function HVACTraining() {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center py-32"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('https://public.readdy.ai/ai/img_res/10b52adffb5f9f3340e7548cbd726bcd.jpg')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            HVAC Training Program
          </h1>
          <p className="text-xl md:text-2xl text-white mb-8 max-w-3xl mx-auto">
            Start Your Journey to Becoming a Certified HVAC Technician
          </p>
          <Button href="/contact" size="lg">
            Enroll Now
          </Button>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-black mb-6">Program Overview</h2>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                Our comprehensive HVAC training program equips you with the essential skills and knowledge needed to excel in the heating, ventilation, and air conditioning industry. Through online learning experiences and real-world applications, you'll master the technical expertise required for a successful career.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Whether you're starting fresh or advancing your career, our program provides the foundation you need to become a certified HVAC professional and meet the growing demand in this essential trade.
              </p>
            </div>
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=hvac%20training%20classroom%20with%20students%20learning%20air%20conditioning%20systems%20hands%20on%20practice%20with%20equipment%20modern%20educational%20setting%20simple%20clean%20background%20bright%20lighting%20professional%20atmosphere%20realistic%20detailed%20photography&width=800&height=600&seq=hvac2&orientation=landscape"
                alt="HVAC Training"
                className="rounded-lg shadow-xl w-full h-auto object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What You'll Learn */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-black text-center mb-12">What You'll Learn</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "System Installation",
                description: "Learn proper installation techniques for residential and commercial HVAC systems, including ductwork and ventilation."
              },
              {
                title: "Maintenance & Repair",
                description: "Master troubleshooting, diagnostics, and repair procedures for all types of heating and cooling equipment."
              },
              {
                title: "Refrigeration Principles",
                description: "Understand refrigeration cycles, refrigerant handling, and EPA certification requirements."
              },
              {
                title: "Electrical Systems",
                description: "Gain expertise in electrical components, wiring, controls, and safety protocols for HVAC systems."
              },
              {
                title: "Energy Efficiency",
                description: "Learn about modern energy-efficient systems, green technologies, and sustainable practices."
              },
              {
                title: "Safety Standards",
                description: "Master OSHA safety requirements, proper tool usage, and industry best practices."
              }
            ].map((item, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-lg">
                <div className="w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-check-line text-2xl text-blue-900"></i>
                </div>
                <h3 className="text-xl font-bold text-black mb-3">{item.title}</h3>
                <p className="text-gray-700">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Career Opportunities */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-black text-center mb-12">Career Opportunities</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {[
              "HVAC Technician",
              "Refrigeration Specialist",
              "HVAC Installer",
              "Service Technician",
              "Maintenance Technician",
              "HVAC Project Manager"
            ].map((career, index) => (
              <div key={index} className="flex items-center bg-gray-50 p-6 rounded-lg">
                <div className="w-10 h-10 bg-yellow-400 rounded-full flex items-center justify-center mr-4">
                  <i className="ri-briefcase-line text-xl text-blue-900"></i>
                </div>
                <span className="text-lg font-semibold text-black">{career}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Program Details */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-black text-center mb-12">Program Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-lg text-center">
              <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-3xl text-blue-900"></i>
              </div>
              <h3 className="text-2xl font-bold text-black mb-3">Flexible Schedule</h3>
              <p className="text-gray-700">Online learning options available to fit your lifestyle</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-lg text-center">
              <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-medal-line text-3xl text-blue-900"></i>
              </div>
              <h3 className="text-2xl font-bold text-black mb-3">Certifications</h3>
              <p className="text-gray-700">
                ✔️ EPA Section 608 Certification – Required for handling refrigerants.<br/>
                ✔️ OSHA 10-Hour Certification – Demonstrates workplace safety knowledge.<br/>
                ✔️ NATE Ready to Work – Industry-recognized HVAC certification.
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-lg text-center">
              <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-tools-line text-3xl text-blue-900"></i>
              </div>
              <h3 className="text-2xl font-bold text-black mb-3">Online Training</h3>
              <p className="text-gray-700">Learn with industry-standard curriculum and virtual instruction</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="py-20 bg-blue-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Start Your HVAC Career?</h2>
          <p className="text-xl text-white mb-8">
            Join our program today and become a certified HVAC professional.
          </p>
          <Button href="/contact" size="lg">
            Contact Us Today
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
